import SubmitCard from '@/components/card/submit-card-component';
import ValidationErrorMessage from '@/components/error/validation-error-component';
import Modal from '@/components/modal/modal-component';
import { recursiveFlatMap } from '@/pages/company-management/company-products/utils';
import { Heading2Text } from '@/utils/global-fonts';
import PropTypes from 'prop-types';
import { useCallback } from 'react';
import { Controller, useForm, useFormContext } from 'react-hook-form';
import { useProductOptions } from '../config-context';
import Styled from '../configuration-style';
import recursiveGetOptionName from '../utils';
import InfoModal from './info-modal';

/**
 * DependencyModal component to handle product dependencies.
 *
 * @param {object} props - The props object.
 * @param {object} props.node - The current node being processed.
 * @param {Function} props.setInfoModal - To open the info modal array.
 * @returns {JSX.Element} The rendered DependencyModal component.
 */
const DependencyModal = ({ node, setInfoModal }) => {
  const {
    control: dependencyControl,
    handleSubmit,
    getValues: getFormValues,
    setValue: setFormValue,
    clearErrors,
    reset,
  } = useForm({
    mode: 'all',
  });
  const { setValue, getValues } = useFormContext();
  const {
    productOptions,
    fetchUEImagesFunc,
    showDependencies,
    setShowDependencies,
  } = useProductOptions();
  const flatOptionsArray = recursiveFlatMap(productOptions || [], []);
  const deps = flatOptionsArray.filter((opt) =>
    node.dependencies?.some((dep) => opt.option_price_link_id === dep),
  );
  // FORCED DEPENDECIES
  const getDependencies = useCallback(
    (conditional) => {
      if (conditional && showDependencies.deselect) {
        return [];
      }
      if (conditional) {
        const x = deps.filter((dep) =>
          deps.find(
            (peerDep) =>
              peerDep.id !== dep.id && peerDep.parent_id === dep.parent_id,
          ),
        );
        return x;
      }
      const x = deps.filter(
        (dep) =>
          !deps.find(
            (peerDep) =>
              peerDep.id !== dep.id && peerDep.parent_id === dep.parent_id,
          ),
      );
      return x;
    },
    [deps, showDependencies.deselect],
  );

  const groupedDependencies = getDependencies(true).reduce((acc, option) => {
    if (!acc[option.parent_id]) {
      acc[option.parent_id] = [];
    }
    acc[option.parent_id].push(option);
    return acc;
  }, {});

  const handleDependencySelect = (field, option) => {
    const { partNumber } = recursiveGetOptionName(option, productOptions, {
      name: '',
      partNumber: '',
    });

    const selectType = flatOptionsArray.find(
      (opt) => opt.part_number === partNumber,
    )?.select_type;
    const isRequired = flatOptionsArray.find(
      (opt) => opt.part_number === partNumber,
    )?.is_required;

    clearErrors();
    if (!field.value && selectType === 'single') {
      const formValues = getFormValues();
      const keys = Object.keys(formValues);
      keys.forEach((key) => {
        if (key !== option.id) {
          setFormValue(key, null);
        }
      });
      field.onChange(option.id);
    } else if (isRequired === 'true') {
      field.onChange(option.id);
    } else {
      field.onChange(field.value ? null : option.id);
    }
  };

  const forcedDependencies = getDependencies(false);

  return (
    <Modal
      open={showDependencies.show}
      close={() =>
        setShowDependencies({
          type: 'DONE',
        })
      }
      width="820px"
      bodyStyle={{ flexDirection: 'column', padding: 0, maxHeight: '80vh' }}
    >
      <Styled.HeaderContainer>
        <Heading2Text>Selection Assistant</Heading2Text>
        <Styled.StyledBodyTextLarge>
          Further adjustments are needed to enable your selection.
        </Styled.StyledBodyTextLarge>
        <Styled.CustomSubheaderText>
          You {showDependencies.deselect ? 'deselected' : 'selected'}{' '}
          {node.name}, this will {showDependencies.deselect ? 'remove' : 'add'}:
        </Styled.CustomSubheaderText>
        {forcedDependencies.map((opt) => (
          <Styled.ForcedOptionContainer key={opt.id}>
            {opt?.thumbnail ? (
              <Styled.ThumbnailImage src={opt.thumbnail} />
            ) : (
              <p>x</p>
            )}
            {opt.name}
          </Styled.ForcedOptionContainer>
        ))}
        {groupedDependencies.length > 0 && (
          <Styled.CustomSubheaderText>
            Select one of the following options
          </Styled.CustomSubheaderText>
        )}
      </Styled.HeaderContainer>
      <Styled.OptionalDependencyComponent>
        {Object.keys(groupedDependencies).map((parentId, i) => (
          <>
            <Styled.Container key={parentId}>
              {groupedDependencies[parentId].map((option) => (
                <Controller
                  key={option.id}
                  rules={{
                    validate: () => {
                      const { isRequired } = option;
                      if (isRequired === 'true') {
                        const values = getFormValues();
                        const keys = Object.keys(values);
                        const x = keys.some((key) => !!values[key]);
                        if (!x) {
                          return 'Please select an option';
                        }
                      }
                      return true;
                    },
                  }}
                  render={({ field }) => (
                    <Styled.OptionContainer>
                      <Styled.OptionCardContainer
                        selected={field.value}
                        id={`${option.id}_${option.part_number}`}
                        onClick={() => {
                          handleDependencySelect(field, option);
                        }}
                      >
                        <Styled.OptionCard>
                          {option.thumbnail ? (
                            <Styled.Img src={option.thumbnail} />
                          ) : (
                            <p>not found</p>
                          )}
                        </Styled.OptionCard>
                        <Styled.OptionNameContainer>
                          <p>{option.name}</p>
                          <Styled.IconHolder>
                            <Styled.RadioDisplay
                              $selected={field.value}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDependencySelect(field, option);
                              }}
                            />
                            <Styled.ActionHolder>
                              <Styled.InfoIcon
                                size={24}
                                relative
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setInfoModal({ node, show: true });
                                }}
                              />
                            </Styled.ActionHolder>
                          </Styled.IconHolder>
                        </Styled.OptionNameContainer>
                      </Styled.OptionCardContainer>
                    </Styled.OptionContainer>
                  )}
                  name={`${option.id}_${option.part_number}`}
                  control={dependencyControl}
                />
              ))}
            </Styled.Container>
            {i + 1 !== Object.keys(groupedDependencies).length && (
              <Styled.CustomSubheaderText>AND</Styled.CustomSubheaderText>
            )}
          </>
        ))}
      </Styled.OptionalDependencyComponent>
      <Styled.FooterComponent>
        <SubmitCard
          padding="0"
          primaryButtonTitle="Accept Changes"
          primaryButtonType="submit"
          primaryButtonWidth="180px"
          submitClick={handleSubmit((conditionalValues) => {
            const handleParentCheck = (pName, parentId, nodeId) => {
              const parentOptionNode = flatOptionsArray.find(
                (q) => q.id === parentId,
              );
              const isFormOptionMulti =
                parentOptionNode?.select_type === 'multi';
              const parent = pName.split('.');
              parent.pop();
              const parentNameString = parent.join('.');
              const parentValue = getValues(parentNameString);
              if (parentValue) {
                if (isFormOptionMulti) {
                  // Here we just need to check if the parent is not selected. If it is not we need to select it.
                  if (!parentValue.id) {
                    parentValue.children = parentValue.children.map((child) => {
                      const retVal = child;
                      if (nodeId === child.node || nodeId === child.id) {
                        retVal.id = child.node ?? child.id;
                      }
                      return retVal;
                    });
                    parentValue.id = parentValue.node ?? parentOptionNode.id;
                    setValue(parentNameString, parentValue, {
                      shouldValidate: true,
                      shouldDirty: true,
                      shouldTouch: true,
                    });
                  }
                } else {
                  parentValue.children = parentValue.children.map((child) => {
                    const retVal = child;
                    if (nodeId === child.node || nodeId === child.id) {
                      retVal.id = child.node ?? child.id;
                    }
                    return retVal;
                  });
                  parentValue.id = parentValue.node ?? parentOptionNode.id;
                  setValue(parentNameString, parentValue, {
                    shouldValidate: true,
                    shouldDirty: true,
                    shouldTouch: true,
                  });
                }
                if (parentOptionNode.parent_id) {
                  const grandParentName = parentNameString.split('.');
                  grandParentName.pop();
                  const grandParentNameString = grandParentName.join('.');
                  handleParentCheck(
                    grandParentNameString,
                    parentOptionNode.parent_id,
                    parentOptionNode.id,
                  );
                }
              } else {
                setValue(`${parentNameString}.id`, parentOptionNode.id);
                setValue(
                  `options.${parentNameString}.partNumber`,
                  parentOptionNode.partNumber,
                );
                setValue(
                  `options.${parentNameString}.type`,
                  parentOptionNode.option_type,
                );
                if (parentOptionNode.parent_id) {
                  const grandParentName = parentNameString.split('.');
                  grandParentName.pop();
                  const grandParentNameString = grandParentName.join('.');
                  handleParentCheck(
                    grandParentNameString,
                    parentOptionNode.parent_id,
                    parentOptionNode.id,
                  );
                }
              }
            };

            if (JSON.stringify(conditionalValues) !== '{}') {
              const keys = Object.keys(conditionalValues);

              keys.forEach((key) => {
                if (conditionalValues[key]) {
                  const [id] = key.split('_');

                  const option = flatOptionsArray.find((opt) => opt.id === id);

                  const {
                    name: condName,
                    dependencies,
                    partNumber,
                  } = recursiveGetOptionName(option, productOptions, {
                    name: 'options',
                    partNumber: '',
                  });

                  if (dependencies?.length > 0) {
                    const isAnySelected = dependencies?.some((child) => {
                      const childOption = flatOptionsArray.find(
                        (opt) => opt.option_price_link_id === child,
                      );
                      const { name: optionName } = recursiveGetOptionName(
                        childOption,
                        productOptions,
                        {
                          name: 'options',
                          partNumber: '',
                        },
                      );
                      const isSelected = getValues(optionName);
                      return isSelected?.id;
                    });
                    if (!isAnySelected) {
                      setShowDependencies({
                        type: 'ADD',
                        payload: option,
                      });
                    } else {
                      setShowDependencies({
                        type: 'DONE',
                      });
                    }
                  }

                  if (option.parent_id) {
                    const parent = condName.split('.');
                    parent.pop();
                    const parentNameString = parent.join('.');
                    handleParentCheck(
                      parentNameString,
                      option.parent_id,
                      option.id,
                    );
                  }

                  setValue(
                    `${condName}.id`,
                    showDependencies.deselect ? null : id,
                  );
                  setValue(`options.${condName}.partNumber`, partNumber);
                  setValue(`options.${condName}.type`, option.option_type);
                }
              });
            }
            if (forcedDependencies.length > 0) {
              forcedDependencies.forEach((dep) => {
                const option = flatOptionsArray.find(
                  (opt) => opt.id === dep.id,
                );
                const {
                  name: forcedName,
                  dependencies,
                  partNumber,
                } = recursiveGetOptionName(option, productOptions, {
                  name: 'options',
                  partNumber: '',
                });

                if (dependencies?.length > 0) {
                  const isAnySelected = dependencies?.some((child) => {
                    const childOption = flatOptionsArray.find(
                      (opt) => opt.option_price_link_id === child,
                    );
                    const { name: optionName } = recursiveGetOptionName(
                      childOption,
                      productOptions,
                      {
                        name: 'options',
                        partNumber: '',
                      },
                    );
                    const isSelected = getValues(optionName);
                    return isSelected?.id;
                  });
                  if (!isAnySelected) {
                    setShowDependencies({
                      type: 'ADD',
                      payload: option,
                    });
                  }
                }
                setValue(
                  `${forcedName}.id`,
                  showDependencies.deselect ? null : dep.id,
                );

                setValue(`options.${forcedName}.partNumber`, partNumber);
                setValue(`options.${forcedName}.type`, option.option_type);
              });
              // handle set value here
            }
            reset();

            setValue(
              `${showDependencies.name}.id`,
              showDependencies.deselect ? null : node.id,
            );

            fetchUEImagesFunc();
            setTimeout(() => {
              setShowDependencies({
                type: 'DONE',
              });
            }, 0);
          })}
          primaryButtonDisabled={
            getDependencies(true).length === 0 &&
            forcedDependencies.length === 0
          }
          secondaryButtonType="button"
          secondaryButtonDisabled={false}
          secondaryButtonWidth="fit-content"
          secondaryButtonTitle="Cancel Selection"
          submitClickSecondary={() => {
            setShowDependencies({
              type: 'DONE',
            });
            setValue(
              `${showDependencies.name}.id`,
              showDependencies.deselect ? node.id : undefined,
              {
                shouldValidate: true,
                shouldDirty: true,
                shouldTouch: true,
              },
            );
            reset();
          }}
        />
      </Styled.FooterComponent>
    </Modal>
  );
};

DependencyModal.propTypes = {
  setInfoModal: PropTypes.func.isRequired,
  node: PropTypes.shape({
    name: PropTypes.string,
    id: PropTypes.string,
    part_number: PropTypes.string,
    dependencies: PropTypes.arrayOf(PropTypes.string),
  }).isRequired,
};

export default DependencyModal;
